import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEmail } from '@components/icons/email';
import { IconPhone } from '@components/icons/phone';
import { IconSms } from '@components/icons/phone-sms';
import { IconRedo } from '@components/icons/redo';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useCreateManualReminder } from 'api/reminders/hooks';
import { ReminderSchedule } from 'api/reminders/requests';
import { TravellerJourneyActionDetailed } from 'api/types';

import {
  REMINDER_SUMMARY_TEMPLATES,
  buildReminderSummaryParts,
  getTimezoneName,
} from './reminder-summary';

export const RemindersContentDetails = ({
  action,
  emailAction,
  smsAction,
}: {
  action: TravellerJourneyActionDetailed;
  emailAction?: TravellerJourneyActionDetailed<'send_email'>;
  smsAction?: TravellerJourneyActionDetailed<'send_sms'>;
}) => {
  const { t } = useScopedTranslate(remindersLocale);

  const formSent = action.status === 'in_progress';
  const formCompleted = action.status === 'completed';

  const reminderData = action.reminder;
  const schedules = action.reminderSchedules ?? [];
  const automaticSchedules = schedules.filter((schedule) => schedule.isAutomatic === true);

  const hasFormCompletedWithNoRemindersSent =
    formCompleted && schedules.every((schedule) => schedule.status !== 'completed');

  const hasNoReminderStarted = schedules.every((schedule) => schedule.status === 'not_started');

  const firstScheduledReminder =
    hasNoReminderStarted && automaticSchedules.length > 0
      ? automaticSchedules.reduce((prev, current) =>
          prev.scheduledDate < current.scheduledDate ? prev : current
        )
      : undefined;
  const sentReminders = schedules.filter(
    (schedule) => schedule.status === 'completed' || schedule.status === 'failed'
  );
  const notStartedReminders = automaticSchedules.filter(
    (schedule) => schedule.status === 'not_started'
  );
  const nextReminder =
    notStartedReminders.length > 0
      ? notStartedReminders.reduce((prev, current) =>
          prev.scheduledDate < current.scheduledDate ? prev : current
        )
      : null;

  const allRemindersSent = sentReminders.length === schedules.length;

  const completedRemindersDates = sentReminders?.map((reminder) => reminder.scheduledDate);
  const uniqueDates = [...new Set(completedRemindersDates)];
  const completedRemindersByDate = uniqueDates.map((date) =>
    sentReminders?.filter((reminder) => reminder.scheduledDate === date)
  );

  const completedAutomaticRemindersByDate = completedRemindersByDate.filter(
    (reminders) => reminders?.[0].isAutomatic
  );
  const completedManualRemindersByDate = completedRemindersByDate.filter(
    (reminders) => !reminders?.[0].isAutomatic
  );

  const getRepeatDescription = () => {
    if (!reminderData?.frequency || !reminderData?.amount) return '';
    if (!reminderData.emailNotify && !reminderData.smsNotify) return '';

    const frequency = Number(reminderData.frequency);
    const parts = buildReminderSummaryParts({
      amount: Number(reminderData.amount),
      frequency,
      hour: reminderData.hours,
      minute: reminderData.minutes,
      allowedWeekdays: reminderData.allowedWeekdays ?? [],
      hasEmail: reminderData.emailNotify,
      hasSms: reminderData.smsNotify,
      timezoneName: getTimezoneName(action.timezone),
    });
    return t(frequency === 1 ? 'repeatSummaryEveryDay' : 'repeatSummaryEveryNDays', parts);
  };

  const createManualReminder = useCreateManualReminder();

  const sendManualReminder = (method: 'email' | 'sms') => {
    const now = new Date();
    const reminderData = {
      travellerAction: action.shortId,
      method: method,
      scheduledDate: now.toISOString(),
      isAutomatic: false,
    };
    createManualReminder.mutateAsync(reminderData);
  };

  return (
    <>
      <Stack space={6}>
        <Stack space={4}>
          {reminderData ? (
            <Stack space={1}>
              <Text font="semibold14" text={t('reminderMethod')} />
              <div>
                {reminderData.emailNotify && (
                  <Stack orientation="horizontal" space={1} align="center">
                    <IconEmail color="elementPurple50" />
                    <Text font="regular14" text={t('email')} />
                  </Stack>
                )}
                {reminderData.smsNotify && (
                  <Stack orientation="horizontal" space={1} align="center">
                    <IconSms color="elementPurple50" />
                    <Text font="regular14" text={t('sms')} />
                  </Stack>
                )}
              </div>
            </Stack>
          ) : (
            <Stack space={1}>
              <Text font="semibold14" text={t('reminders')} />
              <Text font="regular14" text={t('disabled')} color="textHelper" />
            </Stack>
          )}
        </Stack>
        {reminderData?.frequency && (
          <>
            <Separator />
            <Stack space={4}>
              <Stack space={1}>
                <Text font="semibold14" text={t('reminders')} />
                <Text font="regular14" text={getRepeatDescription()} />
              </Stack>
            </Stack>
          </>
        )}
        <Separator />
        <Stack space={4}>
          {hasNoReminderStarted ? (
            <Stack space={4}>
              <div>
                <Text font="semibold14" text={t('sentReminders')} />
                <Text font="regular14" color="textHelper" text={t('noRemindersSent')} />
              </div>
              {firstScheduledReminder && (
                <Text
                  font="regular14"
                  text={t('firstReminder', {
                    date: abbreviateDate(
                      firstScheduledReminder.scheduledDate,
                      'en',
                      'weekday-datetime'
                    ),
                  })}
                />
              )}
            </Stack>
          ) : (
            <Stack space={4}>
              <Text font="semibold14" text={t('sentReminders')} />
              {hasFormCompletedWithNoRemindersSent && (
                <Text
                  font="regular14"
                  color="textHelper"
                  text={t('formCompletedNoRemindersSent')}
                />
              )}
              {completedAutomaticRemindersByDate?.map((reminders, idx) => {
                const emailReminder = reminders?.find((reminder) => reminder.method === 'email');
                const smsReminder = reminders?.find((reminder) => reminder.method === 'sms');
                return (
                  <SentReminders
                    key={idx}
                    emailReminder={emailReminder}
                    smsReminder={smsReminder}
                    idx={idx}
                  />
                );
              })}
              {formSent && reminderData && allRemindersSent && (
                <Stack orientation="horizontal" align="center" space={2}>
                  <IconCheckboxChecked color="green100" />
                  <Text font="regular14" color="green100" text={t('allRemindersSent')} />
                </Stack>
              )}
              {completedManualRemindersByDate?.map((reminders, idx) => {
                const emailReminder = reminders?.find((reminder) => reminder.method === 'email');
                const smsReminder = reminders?.find((reminder) => reminder.method === 'sms');
                return (
                  <SentReminders
                    key={idx}
                    emailReminder={emailReminder}
                    smsReminder={smsReminder}
                    idx={idx}
                    isAutomatic={false}
                  />
                );
              })}
            </Stack>
          )}
          {!firstScheduledReminder && nextReminder && (
            <Text
              font="regular14"
              text={t('nextReminder', {
                date: abbreviateDate(nextReminder?.scheduledDate, 'en', 'weekday-datetime'),
              })}
            />
          )}
        </Stack>
        <Stack align="flex-end">
          <Menu
            buttonProps={{
              text: t('sendReminder'),
              iconLeft: <IconRedo />,
              variant: 'secondary',
              iconRight: <IconDropdown />,
              tooltip: formCompleted ? t('formCompleted') : !formSent ? t('formNotSent') : '',
              disabled: !formSent || formCompleted,
            }}
            placement="bottom"
          >
            <Menu.Item
              label={t('sendsmsReminder')}
              icon={<IconPhone />}
              onSelect={() => sendManualReminder('sms')}
              isDisabled={smsAction?.isDisabled}
              tooltip={smsAction?.isDisabled ? t('noSmsNotification') : ''}
            />
            <Menu.Item
              label={t('sendEmailReminder')}
              icon={<IconEmail />}
              onSelect={() => sendManualReminder('email')}
              isDisabled={emailAction?.isDisabled}
              tooltip={emailAction?.isDisabled ? t('noEmailNotification') : ''}
            />
          </Menu>
        </Stack>
      </Stack>
    </>
  );
};

const SentReminders = ({
  emailReminder,
  smsReminder,
  idx,
  isAutomatic = true,
}: {
  emailReminder?: ReminderSchedule;
  smsReminder?: ReminderSchedule;
  idx: number;
  isAutomatic?: boolean;
}) => {
  const { t } = useScopedTranslate(remindersLocale);

  return (
    <Stack key={idx}>
      <Text font="semibold14" text={isAutomatic ? t('automaticReminders') : t('manualReminders')} />
      {emailReminder && <Reminder reminder={emailReminder} type="email" />}
      {smsReminder && <Reminder reminder={smsReminder} type="sms" />}
    </Stack>
  );
};

const Reminder = ({ reminder, type }: { reminder: ReminderSchedule; type: 'email' | 'sms' }) => {
  const { t } = useScopedTranslate(remindersLocale);
  const statusColor = reminder.status === 'completed' ? 'elementGreen200' : 'elementRed100';
  const date = reminder.status === 'completed' ? reminder.dateSucceeded : reminder.dateFailed;

  return date ? (
    <Stack space={2} orientation="horizontal" justify="space-between">
      <Stack orientation="horizontal" align="center">
        <Text
          font="regular14"
          text={type === 'email' ? t('emailSent') : t('smsSent')}
          color="textSecondary"
        />
        <Text font="regular14" text={t(reminder.status)} color={statusColor} />
      </Stack>
      <Text font="regular14" color={statusColor} text={abbreviateDate(date, 'en', 'datetime')} />
    </Stack>
  ) : null;
};

const remindersLocale = {
  en: {
    disabled: 'Disabled',
    reminderMethod: 'Reminder method',
    email: 'Email',
    sms: 'SMS',
    reminders: 'Reminders',
    repeatSummaryEveryDay: REMINDER_SUMMARY_TEMPLATES.everyDay,
    repeatSummaryEveryNDays: REMINDER_SUMMARY_TEMPLATES.everyNDays,
    noRemindersSent: 'No reminders have been sent',
    formCompletedNoRemindersSent: 'No reminders were sent because the form has been completed',
    sentReminders: 'Sent reminders',
    sendReminder: 'Send manual reminder',
    sendsmsReminder: 'Send SMS reminder',
    sendEmailReminder: 'Send email reminder',
    formNotSent: 'Form has not been sent',
    noSmsNotification: 'SMS was not selected as a notification method',
    noEmailNotification: 'Email was not selected as a notification method',
    formCompleted: 'Form has been completed',
    firstReminder: 'First automatic reminder is scheduled to be sent on {date}',
    automaticReminders: 'Automatic reminders',
    manualReminders: 'Manual reminders',
    emailSent: 'Email • ',
    smsSent: 'SMS • ',
    not_started: 'Not started',
    in_progress: 'In progress',
    sent: 'Sent',
    completed: 'Delivered',
    failed: 'Delivery failure',
    skipped: 'Skipped',
    nextReminder: 'Next automatic reminder is scheduled to be sent on {date}',
    allRemindersSent: 'All automatic reminders have been sent',
  },
};
